import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  FlatList,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useRoute } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { vaultService } from '../../../services/vaultService';

import { createLogger } from '../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../services/encryption/credentialFields';
const logger = createLogger('PasswordsInput');

const categories = [
  'Financial',
  'Social',
  'Utilities',
  'Communication',
  'Insurance',
  'Medical',
  'Ecommerce',
  'Memberships',
  'Security',
  'Digital',
  'Other'
];

const passwordTypes: { [key: string]: string[] } = {
  Financial: ['Banking', 'Investment', 'Credit Card', 'Loan', 'Cash App'],
  Social: ['Social Media', 'Business', 'Website', 'Dating App'],
  Utilities: ['Streaming TV', 'Electricity', 'Gas', 'Water', 'Garbage', 'Internet', 'App Subscriptions'],
  Communication: ['Mobile Phone', 'Email', 'Data Storage', 'Other'],
  Insurance: ['Health', 'Life', 'Home', 'Auto', 'Pet', 'Work'],
  Medical: ['Patient Portal', 'Prescription', 'Fitness', 'Health', 'Other'],
  Ecommerce: ['Amazon', 'Ebay', 'Etsy', 'Online Retailer', 'Subscription', 'Other'],
  Memberships: ['Online Course', 'Professional', 'Club', 'Gym', 'Other'],
  Security: ['Home Security', 'VPN', 'Authenticator', 'Password App'],
  Digital: ['Music', 'Photos', 'Domain Name', 'Legal', 'Other'],
  Other: ['Travel', 'Rewards', 'Pet', 'Misc']
};

export const PasswordsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute();
  const [formData, setFormData] = useState({
    category: '',
    passwordType: '',
    loginName: '',
    password: '',
    websiteUrl: '',
    notes: '',
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Get category from route params if available
  useEffect(() => {
    const params = route.params as any;
    if (params?.category) {
      setFormData(prev => ({ ...prev, category: params.category }));
    }
  }, [route.params]);

  const handleSubmit = async () => {
    if (!formData.category || !formData.passwordType || !formData.loginName || !formData.password) {
      Alert.alert('Missing Information', 'Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    // K5: a login is saved only under a Recovery Code; ask before anything is written.
    if (!(await ensureCredentialWriteKey())) {
      setIsSubmitting(false);
      return;
    }
    try {
      const passwordId = `password_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      await vaultService.storePasswordCredentials(passwordId, {
        category: formData.category,
        passwordType: formData.passwordType,
        username: formData.loginName,
        password: formData.password,
        websiteUrl: formData.websiteUrl,
        notes: formData.notes,
      });

      Alert.alert(
        'Success',
        'Password saved successfully!',
        [
          {
            text: 'OK',
            onPress: () => navigation.navigate('PasswordsDataScreen' as any, { passwordId })
          }
        ]
      );
    } catch (error: any) {
      logger.error('Error saving password:', error);
      let errorMessage = 'Failed to save password. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`;
      } else if (error.request) {
        // Network error - no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
      } else {
        // Other error
        errorMessage = `Error: ${error.message || 'Unknown error occurred'}`;
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategorySelect = (category: string) => {
    setFormData({ ...formData, category, passwordType: '' }); // Reset password type when category changes
    setShowCategoryModal(false);
  };

  const handleTypeSelect = (type: string) => {
    setFormData({ ...formData, passwordType: type });
    setShowTypeModal(false);
  };

  const availableTypes = formData.category ? passwordTypes[formData.category] : [];

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false} automaticallyAdjustKeyboardInsets={true}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={[styles.backIcon, { tintColor: '#333' }]}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.manageText}>Add</Text>
          <Text style={styles.allPasswordsText}>Password Details</Text>
          <View style={styles.encryptionNotice}>
            <Image
              source={require('../../../assets/icons/lock.png')}
              style={styles.encryptionIcon}
            />
            <Text style={styles.encryptionText}>denotes stored in encrypted vault</Text>
          </View>
        </View>

        <View style={styles.formContainer}>
          {/* Category Selection */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Category *</Text>
            <TouchableOpacity
              style={styles.dropdownInput}
              onPress={() => setShowCategoryModal(true)}
            >
              <Text style={[styles.inputText, !formData.category && styles.placeholderText]}>
                {formData.category || 'Select Category'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          {/* Password Type Selection */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Password Type *</Text>
            <TouchableOpacity
              style={[styles.dropdownInput, !formData.category && styles.disabledDropdown]}
              onPress={() => formData.category && setShowTypeModal(true)}
              disabled={!formData.category}
            >
              <Text style={[styles.inputText, !formData.passwordType && styles.placeholderText]}>
                {formData.passwordType || 'Select Type'}
              </Text>
              <Image
                source={require('../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          {/* Username/Login Name */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Username/Login Name *</Text>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.loginName}
                onChangeText={(text) => setFormData({ ...formData, loginName: text })}
                placeholder="Enter username or login name"
                placeholderTextColor="#A9A9A9"
              />
              <Image
                source={require('../../../assets/icons/lock.png')}
                style={styles.lockIcon}
              />
            </View>
          </View>

          {/* Password */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Password *</Text>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.password}
                onChangeText={(text) => setFormData({ ...formData, password: text })}
                placeholder="Enter password"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showPassword}
              />
              <TouchableOpacity
                onPress={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
              >
                <Image
                  source={showPassword ? 
                    require('../../../assets/icons/Eye.png') :
                    require('../../../assets/icons/Eye-closed.png')
                  }
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
              <Image
                source={require('../../../assets/icons/lock.png')}
                style={styles.lockIcon}
              />
            </View>
          </View>

          {/* Website URL */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website URL</Text>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={formData.websiteUrl}
                onChangeText={(text) => setFormData({ ...formData, websiteUrl: text })}
                placeholder="Enter website URL (optional)"
                placeholderTextColor="#A9A9A9"
                keyboardType="url"
                autoCapitalize="none"
              />
            </View>
          </View>

          {/* Notes */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <View style={styles.inputWrapper}>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={formData.notes}
                onChangeText={(text) => setFormData({ ...formData, notes: text })}
                placeholder="Add any additional notes (optional)"
                placeholderTextColor="#A9A9A9"
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>
          </View>

          {/* Submit Button */}
          <TouchableOpacity
            style={[styles.submitButton, isSubmitting && styles.disabledButton]}
            onPress={handleSubmit}
            disabled={isSubmitting}
          >
            <Text style={styles.submitButtonText}>
              {isSubmitting ? 'Saving...' : 'Save Password'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Category Modal */}
      <Modal
        visible={showCategoryModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowCategoryModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Category</Text>
              <TouchableOpacity onPress={() => setShowCategoryModal(false)}>
                <Image
                  source={require('../../../assets/icons/close.png')}
                  style={styles.closeIcon}
                />
              </TouchableOpacity>
            </View>
            <FlatList
              data={categories}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.modalItem}
                  onPress={() => handleCategorySelect(item)}
                >
                  <Text style={styles.modalItemText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Password Type Modal */}
      <Modal
        visible={showTypeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowTypeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Type</Text>
              <TouchableOpacity onPress={() => setShowTypeModal(false)}>
                <Image
                  source={require('../../../assets/icons/close.png')}
                  style={styles.closeIcon}
                />
              </TouchableOpacity>
            </View>
            <FlatList
              data={availableTypes}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.modalItem}
                  onPress={() => handleTypeSelect(item)}
                >
                  <Text style={styles.modalItemText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '25%',
  },
  notificationIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  titleContainer: {
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
  },
  manageText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  allPasswordsText: {
    fontSize: ms(24, 0.3),
    color: '#FF9F5A',
    fontWeight: '600',
  },
  encryptionNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: ms(8),
  },
  encryptionIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#29BFC0',
    marginRight: ms(6),
  },
  encryptionText: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    fontStyle: 'italic',
  },
  formContainer: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(120),
  },
  inputGroup: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
    fontWeight: '500',
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    paddingHorizontal: ms(15),
  },
  input: {
    flex: 1,
    height: s(50),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  textArea: {
    height: s(80),
    textAlignVertical: 'top',
  },
  dropdownInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    paddingHorizontal: ms(15),
    height: s(50),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  disabledDropdown: {
    backgroundColor: '#F5F5F5',
    borderColor: '#e8e8e8',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  lockIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  eyeButton: {
    padding: ms(8),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    marginTop: ms(20),
  },
  disabledButton: {
    opacity: 0.5,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#EEEEEE',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  closeIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#1C2C4F',
  },
  modalItem: {
    paddingHorizontal: ms(20),
    paddingVertical: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#EEEEEE',
  },
  modalItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});

export default PasswordsInputScreen;
